import { useFormatters, useTranslation } from "@ferrivia/i18n";
import type { ReadClientPlanItem } from "@ferrivia/api-contract";
import { Apple, CalendarCheck, ClipboardList, FlaskConical, Pill, type LucideIcon } from "lucide-react";

const KIND_ICON: Record<string, LucideIcon> = {
  supplement: Pill,
  diet: Apple,
  lab: FlaskConical,
  visit: CalendarCheck,
};

export function kindIcon(kind: string): LucideIcon {
  return KIND_ICON[kind] ?? ClipboardList;
}

// A date-only YYYY-MM-DD read as a local calendar date: new Date(str) would
// parse it as UTC midnight and shift the day west of Greenwich.
function localDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

// Copy shared by the Home row and the sheet. Dates are absolute and nothing
// here compares one with today: a past check-in reads exactly like a future one.
export function usePlanCopy() {
  const { t } = useTranslation("client");
  const { date } = useFormatters();

  const formatDate = (iso: string) => date.format(localDate(iso));

  // Static lookups: i18next-parser only extracts literal-key t() calls.
  const titles: Record<string, string> = {
    physician: t("home.blocks.plan.authorTitle.physician", "lääkäri"),
    nutrition_coach: t("home.blocks.plan.authorTitle.nutritionCoach", "ravitsemusvalmentaja"),
  };
  const channels: Record<string, string> = {
    chat: t("home.blocks.plan.channel.chat", "chatissa"),
    video_visit: t("home.blocks.plan.channel.videoVisit", "videovastaanotolla"),
    lab_retest: t("home.blocks.plan.channel.labRetest", "uusintakokeen jälkeen"),
  };

  const authorLine = (item: ReadClientPlanItem) => {
    const title = titles[item.authorTitle];
    return title
      ? t("home.blocks.plan.author", "Suosittelee {{author}}, {{title}}", { author: item.authorName, title })
      : t("home.blocks.plan.authorNameOnly", "Suosittelee {{author}}", { author: item.authorName });
  };

  // The sheet's signature: the name and title are self-evidently the author,
  // so no "Suosittelee" in front.
  const signature = (item: ReadClientPlanItem) => {
    const title = titles[item.authorTitle];
    const started = formatDate(item.startedOn);
    return title
      ? t("home.blocks.plan.signature", "{{author}}, {{title}} · {{date}}", { author: item.authorName, title, date: started })
      : t("home.blocks.plan.signatureNameOnly", "{{author}} · {{date}}", { author: item.authorName, date: started });
  };

  const dateLine = (item: ReadClientPlanItem) => {
    if (item.checkInOn === null) return null;
    const d = formatDate(item.checkInOn);
    return item.kind === "lab"
      ? t("home.blocks.plan.drawBefore", "Verikoe ennen {{date}}", { date: d })
      : t("home.blocks.plan.checkIn", "Seuranta {{date}}", { date: d });
  };

  const channelText = (item: ReadClientPlanItem) =>
    item.checkInChannel === null ? null : (channels[item.checkInChannel] ?? null);

  const durationLine = (item: ReadClientPlanItem) =>
    item.runsForWeeks === null
      ? t("home.blocks.plan.ongoing", "Toistaiseksi")
      : t("home.blocks.plan.duration", {
          count: item.runsForWeeks,
          date: formatDate(item.startedOn),
          defaultValue: "{{count}} viikkoa {{date}} alkaen",
        });

  return { authorLine, signature, dateLine, channelText, durationLine };
}
